import { ApiService } from '../../api/api-service';
import {
  BOMHardware,
  BOMSpecificationsData,
  BundleCost,
  BundleCostSearchRequest,
  MaterialGroup,
  MaterialGroupOption,
  PriceVersion,
  ProductPriceSheetData,
} from './types';

const SERVICE_ROOT = '/price-management';

/**
 * Convert a number[] (byte array from the API) to a Base64 string for sending back.
 * .NET System.Text.Json serialises byte[] as Base64, so we must match that format.
 */
function toBase64(bytes: number[]): string {
  return btoa(bytes.map(b => String.fromCharCode(b)).join(''));
}

/**
 * Service for price management bundle cost operations
 */
export class PriceManagementService extends ApiService {
  /**
   * Get a single bundle cost by ID
   * @param id - The bundle cost ID
   */
  async getBundleCostById(id: number): Promise<BundleCost> {
    return this.handleApiCall(
      () => this.client.get(`${SERVICE_ROOT}/bundle-costs/${id}`),
      `Error fetching bundle cost ${id}`
    );
  }

  /**
   * Get all bundle costs for a price version
   * @param versionId - The price version ID
   * @param materialGroup - Optional material group filter
   */
  async getBundleCosts(versionId: number, materialGroup?: string): Promise<BundleCost[]> {
    const params = materialGroup ? { materialGroup } : {};
    return this.handleApiCall(
      () => this.client.get(`${SERVICE_ROOT}/bundle-costs`, { params: { versionId, ...params } }),
      `Error fetching bundle costs for version ${versionId}`
    );
  }

  /**
   * Search bundle costs with advanced filtering and sorting
   * @param request - Search criteria including filters and sort options
   */
  async searchBundleCosts(request: BundleCostSearchRequest): Promise<BundleCost[]> {
    return this.handleApiCall(
      () => this.client.post(`${SERVICE_ROOT}/bundle-costs/search`, request),
      'Error searching bundle costs'
    );
  }

  /**
   * Get distinct material groups for a price version with item counts
   * @param versionId - The price version ID
   */
  async getMaterialGroups(versionId: number): Promise<MaterialGroup[]> {
    return this.handleApiCall(
      () =>
        this.client.get(`${SERVICE_ROOT}/bundle-costs/material-groups`, { params: { versionId } }),
      `Error fetching material groups for version ${versionId}`
    );
  }

  /**
   * Get all active material groups from the reference table (for Add modal dropdowns)
   */
  async getAllMaterialGroupOptions(): Promise<MaterialGroupOption[]> {
    return this.handleApiCall(
      () => this.client.get(`${SERVICE_ROOT}/material-groups`),
      'Error fetching all material group options'
    );
  }

  /**
   * Get price version by ID
   * @param versionId - The price version ID
   */
  async getVersionById(versionId: number): Promise<PriceVersion> {
    return this.handleApiCall(
      () => this.client.get(`${SERVICE_ROOT}/versions/${versionId}`),
      `Error fetching price version ${versionId}`
    );
  }

  /**
   * Get all price versions for a manufacturer
   * @param manufacturerId - The manufacturer ID
   */
  async getVersionsByManufacturerId(manufacturerId: number): Promise<PriceVersion[]> {
    return this.handleApiCall(
      () => this.client.get(`${SERVICE_ROOT}/versions/manufacturer/${manufacturerId}`),
      `Error fetching price versions for manufacturer ${manufacturerId}`
    );
  }

  /**
   * Create a new empty version
   * @param manufacturerId - The manufacturer ID
   */
  async createEmptyVersion(manufacturerId: number): Promise<PriceVersion> {
    return this.handleApiCall(
      () => this.client.post(`${SERVICE_ROOT}/versions/empty`, { manufacturerId }),
      `Error creating empty version for manufacturer ${manufacturerId}`
    );
  }

  /**
   * Create a new version by copying from an existing version
   * @param sourceVersionId - The source version ID to copy from
   * @param changeDescription - Description of changes in the new version
   * @param effectiveDate - Effective date for the new version (ISO date string)
   */
  async createVersionFromCopy(
    sourceVersionId: number,
    changeDescription: string,
    effectiveDate: string
  ): Promise<PriceVersion> {
    return this.handleApiCall(
      () =>
        this.client.post(`${SERVICE_ROOT}/versions/copy`, {
          sourceVersionId,
          changeDescription,
          effectiveDate,
        }),
      `Error creating version from copy of version ${sourceVersionId}`
    );
  }

  /**
   * Get all BOM hardware items for a version
   * @param versionId - The price version ID
   */
  async getBOMHardware(versionId: number): Promise<BOMHardware[]> {
    return this.handleApiCall(
      () => this.client.get(`${SERVICE_ROOT}/bom-hardware`, { params: { versionId } }),
      `Error fetching BOM hardware for version ${versionId}`
    );
  }

  /**
   * Create a new BOM hardware item
   */
  async createBOMHardware(request: {
    versionId: number;
    hardwareType: string;
    description: string;
    costPerBox: number;
    itemsPerBox: number;
    quantity: number;
    notes?: string;
  }): Promise<BOMHardware> {
    return this.handleApiCall(
      () => this.client.post(`${SERVICE_ROOT}/bom-hardware`, request),
      'Error creating BOM hardware'
    );
  }

  /**
   * Update an existing BOM hardware item (only costPerBox and notes are changed by the server)
   * The DTO requires all fields for model validation even though identity fields are not updated.
   */
  async updateBOMHardware(
    id: number,
    request: {
      hardwareType: string;
      description: string;
      costPerBox: number;
      itemsPerBox: number;
      quantity: number;
      notes?: string;
    }
  ): Promise<BOMHardware> {
    const body = {
      hardwareType: request.hardwareType,
      description: request.description,
      costPerBox: request.costPerBox,
      itemsPerBox: request.itemsPerBox,
      quantity: request.quantity,
      notes: request.notes ?? null,
      rowVersion: toBase64([]),
    };
    return this.handleApiCall(
      () => this.client.put(`${SERVICE_ROOT}/bom-hardware/${id}`, body),
      `Error updating BOM hardware ${id}`
    );
  }

  /**
   * Delete a BOM hardware item
   */
  async deleteBOMHardware(id: number): Promise<void> {
    return this.handleApiCall(
      () => this.client.delete(`${SERVICE_ROOT}/bom-hardware/${id}`),
      `Error deleting BOM hardware ${id}`
    );
  }

  /**
   * Validate that HardwareType + Description combination is unique within a version
   */
  async validateBOMHardwareUniqueness(request: {
    versionId: number;
    hardwareType: string;
    description: string;
    excludeId?: number;
  }): Promise<boolean> {
    return this.handleApiCall(
      () => this.client.post<boolean>(`${SERVICE_ROOT}/bom-hardware/validate-uniqueness`, request),
      'Error validating BOM hardware uniqueness'
    );
  }

  /**
   * Create a new bundle cost
   */
  async createBundleCost(request: {
    versionId: number;
    materialGroup: string;
    size: string;
    bundleCost: number;
    sticksPerBundle?: number;
    feetPerStick?: number;
    sheetsPerBundle?: number;
    itemsPerSheet?: number;
    itemsPerStick?: number;
    notes?: string;
  }): Promise<BundleCost> {
    return this.handleApiCall(
      () => this.client.post(`${SERVICE_ROOT}/bundle-costs`, request),
      'Error creating bundle cost'
    );
  }

  /**
   * Update an existing bundle cost
   */
  async updateBundleCost(
    id: number,
    request: {
      bundleCost: number;
      sticksPerBundle?: number;
      feetPerStick?: number;
      sheetsPerBundle?: number;
      itemsPerSheet?: number;
      itemsPerStick?: number;
      notes?: string;
      rowVersion?: number[];
    }
  ): Promise<BundleCost> {
    const body = {
      bundleCost: request.bundleCost,
      sticksPerBundle: request.sticksPerBundle ?? null,
      feetPerStick: request.feetPerStick ?? null,
      sheetsPerBundle: request.sheetsPerBundle ?? null,
      itemsPerSheet: request.itemsPerSheet ?? null,
      itemsPerStick: request.itemsPerStick ?? null,
      notes: request.notes ?? null,
      rowVersion: toBase64(request.rowVersion ?? []),
    };
    return this.handleApiCall(
      () => this.client.put(`${SERVICE_ROOT}/bundle-costs/${id}`, body),
      `Error updating bundle cost ${id}`
    );
  }

  /**
   * Delete a bundle cost
   */
  async deleteBundleCost(id: number): Promise<void> {
    return this.handleApiCall(
      () => this.client.delete(`${SERVICE_ROOT}/bundle-costs/${id}`),
      `Error deleting bundle cost ${id}`
    );
  }

  /**
   * Validate that a size is unique within a material group and version
   */
  async validateSizeUniqueness(request: {
    versionId: number;
    materialGroup: string;
    size: string;
    excludeId?: number;
  }): Promise<boolean> {
    return this.handleApiCall(
      () => this.client.post<boolean>(`${SERVICE_ROOT}/bundle-costs/validate-uniqueness`, request),
      'Error validating size uniqueness'
    );
  }

  /**
   * Delete a draft version
   */
  async deleteVersion(versionId: number): Promise<void> {
    return this.handleApiCall(
      () => this.client.delete(`${SERVICE_ROOT}/versions/${versionId}`),
      `Error deleting version ${versionId}`
    );
  }

  /**
   * Publish a draft version
   */
  async publishVersion(
    versionId: number,
    rowVersion: number[],
    changeDescription: string
  ): Promise<PriceVersion> {
    return this.handleApiCall(
      () =>
        this.client.post(`${SERVICE_ROOT}/versions/${versionId}/publish`, {
          rowVersion: toBase64(rowVersion),
          changeDescription,
        }),
      `Error publishing version ${versionId}`
    );
  }

  /**
   * Bulk update bundle costs by percentage
   */
  async bulkUpdateBundleCostsByPercentage(
    versionId: number,
    percentageChange: number,
    materialGroup?: string
  ): Promise<{ updatedCount: number; cappedCount: number; message: string }> {
    return this.handleApiCall(
      () =>
        this.client.post(
          `${SERVICE_ROOT}/versions/${versionId}/bundle-costs/bulk-update/percentage`,
          { percentageChange, materialGroup: materialGroup ?? null }
        ),
      `Error bulk updating bundle costs by percentage for version ${versionId}`
    );
  }

  /**
   * Bulk update bundle costs by fixed amount
   */
  async bulkUpdateBundleCostsByFixedAmount(
    versionId: number,
    fixedAmount: number,
    materialGroup?: string
  ): Promise<{ updatedCount: number; cappedCount: number; message: string }> {
    return this.handleApiCall(
      () =>
        this.client.post(
          `${SERVICE_ROOT}/versions/${versionId}/bundle-costs/bulk-update/fixed-amount`,
          { fixedAmount, materialGroup: materialGroup ?? null }
        ),
      `Error bulk updating bundle costs by fixed amount for version ${versionId}`
    );
  }

  /**
   * Bulk update BOM hardware by percentage
   */
  async bulkUpdateBOMHardwareByPercentage(
    versionId: number,
    percentageChange: number
  ): Promise<{ updatedCount: number; cappedCount: number; message: string }> {
    return this.handleApiCall(
      () =>
        this.client.post(
          `${SERVICE_ROOT}/versions/${versionId}/bom-hardware/bulk-update/percentage`,
          { percentageChange }
        ),
      `Error bulk updating BOM hardware by percentage for version ${versionId}`
    );
  }

  /**
   * Bulk update BOM hardware by fixed amount
   */
  async bulkUpdateBOMHardwareByFixedAmount(
    versionId: number,
    fixedAmount: number
  ): Promise<{ updatedCount: number; cappedCount: number; message: string }> {
    return this.handleApiCall(
      () =>
        this.client.post(
          `${SERVICE_ROOT}/versions/${versionId}/bom-hardware/bulk-update/fixed-amount`,
          { fixedAmount }
        ),
      `Error bulk updating BOM hardware by fixed amount for version ${versionId}`
    );
  }

  /**
   * Get product price sheet for a manufacturer
   * @param manufacturerId - The manufacturer ID
   * @param riskLevel - Optional risk level filter
   * @param category - Optional category filter
   */
  async getProductPriceSheet(
    manufacturerId: number,
    riskLevel?: string,
    category?: string
  ): Promise<ProductPriceSheetData> {
    const params: Record<string, string> = {};
    if (riskLevel) params.riskLevel = riskLevel;
    if (category) params.category = category;
    return this.handleApiCall(
      () =>
        this.client.get(`/api${SERVICE_ROOT}/price-sheet/manufacturer/${manufacturerId}`, {
          params,
        }),
      `Error fetching price sheet for manufacturer ${manufacturerId}`
    );
  }

  /**
   * Get the read-only BOM Specifications of a manufacturer, with costs calculated from the latest published prices
   * @param manufacturerId - The manufacturer ID
   * @param riskLevel - Optional risk level filter (I or II)
   * @param productType - Optional product type filter (Gable, Lean-To, Header)
   */
  async getBOMSpecifications(
    manufacturerId: number,
    riskLevel?: string,
    productType?: string
  ): Promise<BOMSpecificationsData> {
    const params: Record<string, string> = {};
    if (riskLevel) params.riskLevel = riskLevel;
    if (productType) params.productType = productType;
    return this.handleApiCall(
      () =>
        this.client.get(`/api${SERVICE_ROOT}/bom/manufacturer/${manufacturerId}`, {
          params,
        }),
      `Error fetching BOM specifications for manufacturer ${manufacturerId}`
    );
  }

  /**
   * Update price settings for a version
   * @param versionId - The price version ID
   * @param fullyBurdenedLaborRate - Fully burdened labor rate
   * @param productionFeetPerHour - Production feet per hour
   */
  async updatePriceSettings(
    versionId: number,
    fullyBurdenedLaborRate: number,
    productionFeetPerHour: number
  ): Promise<PriceVersion> {
    return this.handleApiCall(
      () =>
        this.client.put(`${SERVICE_ROOT}/versions/${versionId}/price-settings`, {
          fullyBurdenedLaborRate,
          productionFeetPerHour,
        }),
      `Error updating price settings for version ${versionId}`
    );
  }
}
